import type { Category, Item, ItemQuery, Page, Supplier, SupplierQuery } from '@iobiz/shared'
import { dbFor, type LocalRow } from '../db.js'

/**
 * Lecture du catalogue depuis la réplique locale.
 *
 * Même parti pris que pour les clients : les pages ne savent pas d'où vient la
 * donnée, et la recherche locale est volontairement plus simple que celle du
 * serveur (sous-chaîne normalisée, pas de tolérance aux fautes de frappe).
 */

function fold(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

export function toItem(row: LocalRow, categories: Map<string, string>, suppliers: Map<string, string>): Item {
  const categoryId = (row['category_id'] as string) ?? null
  const supplierId = (row['supplier_id'] as string) ?? null
  return {
    id: row.id,
    kind: (row['kind'] as Item['kind']) ?? 'product',
    sku: (row['sku'] as string) ?? null,
    barcode: (row['barcode'] as string) ?? null,
    name: String(row['name'] ?? ''),
    description: (row['description'] as string) ?? null,
    visibleSurSite: Boolean(row['visible_site']),
    categoryId,
    categoryName: categoryId ? (categories.get(categoryId) ?? null) : null,
    supplierId,
    supplierName: supplierId ? (suppliers.get(supplierId) ?? null) : null,
    taxId: (row['tax_id'] as string) ?? null,
    /**
     * ⚠️ Défaut à ZÉRO, jamais `undefined`. Les articles répliqués avant que la
     * TVA n'existe n'ont pas cette colonne ; un `undefined` traversait jusqu'au
     * calcul du prix TTC, produisait `NaN`, et faisait planter la fiche article
     * — le code était juste, seule la donnée ancienne manquait.
     */
    taxRate: Number(row['tax_rate'] ?? 0),
    unit: (row['unit'] as Item['unit']) ?? 'piece',
    purchasePrice: Number(row['purchase_price'] ?? 0),
    salePrice: Number(row['sale_price'] ?? 0),
    currencyCode: (row['currency_code'] as string) ?? null,
    trackStock: Boolean(row['track_stock']),
    durationMinutes: (row['duration_minutes'] as number) ?? null,
    minStockLevel: Number(row['min_stock_level'] ?? 0),
    status: (row['status'] as Item['status']) ?? 'active',
    imageUrl: (row['image_url'] as string) ?? null,
    images: (row['images'] as string[]) ?? [],
    createdAt: String(row['created_at'] ?? ''),
    updatedAt: String(row['updated_at'] ?? ''),
    version: row.version,
  } as Item
}

export function toSupplier(row: LocalRow, itemCount = 0): Supplier {
  return {
    id: row.id,
    code: (row['code'] as string) ?? null,
    companyName: String(row['company_name'] ?? ''),
    contactName: (row['contact_name'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    website: (row['website'] as string) ?? null,
    addressLine1: (row['address_line1'] as string) ?? null,
    postalCode: (row['postal_code'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    countryCode: (row['country_code'] as string) ?? null,
    taxId: (row['tax_id'] as string) ?? null,
    paymentTermsDays: Number(row['payment_terms_days'] ?? 0),
    notes: (row['notes'] as string) ?? null,
    itemCount,
    createdAt: String(row['created_at'] ?? ''),
    updatedAt: String(row['updated_at'] ?? ''),
    version: row.version,
  } as Supplier
}

export function toCategory(row: LocalRow): Category {
  return {
    id: row.id,
    parentId: (row['parent_id'] as string) ?? null,
    name: String(row['name'] ?? ''),
    slug: String(row['slug'] ?? ''),
    path: String(row['path'] ?? ''),
    color: (row['color'] as string) ?? null,
    position: Number(row['position'] ?? 0),
    kind: (row['kind'] as Category['kind']) ?? 'product',
    // ⭐ Les postes descendent dans la réplique avec leur catégorie : c'est ce
    // qui rend la prise de rendez-vous possible SANS RÉSEAU. Sans eux,
    // l'agenda ne saurait pas sur quelle place réserver.
    postes: [...((row['postes'] as Category['postes']) ?? [])].sort(
      (a, b) => a.position - b.position,
    ),
    itemCount: 0,
    productCount: 0,
    serviceCount: 0,
    children: [],
  }
}

/** Index id → libellé, pour rattacher catégorie et fournisseur à chaque article. */
async function libelles(storeId: string): Promise<{
  categories: Map<string, string>
  suppliers: Map<string, string>
}> {
  const db = dbFor(storeId)
  const [cats, sups] = await Promise.all([db.categories.toArray(), db.suppliers.toArray()])
  return {
    categories: new Map(cats.map((c) => [c.id, String(c['name'] ?? '')])),
    suppliers: new Map(sups.map((s) => [s.id, String(s['company_name'] ?? '')])),
  }
}

/**
 * État de stock d'un article, règle UNIQUE de l'application.
 *
 * ⚠️ Elle était recopiée dans le tableau de bord et dans la liste du catalogue.
 * Les deux ont divergé : la carte « Produits en alerte » et la colonne « Stock »
 * ne comptaient pas la même chose, et cliquer de l'une vers l'autre aurait
 * ramené un nombre différent de lignes — le genre d'écart qu'on met une heure
 * à croire.
 */
export function etatDeStock(item: Item, quantite: number): EtatStock {
  if (!item.trackStock) return 'nonSuivi'
  if (quantite <= 0) return 'rupture'
  if (item.minStockLevel > 0 && quantite <= item.minStockLevel) return 'alerte'
  return 'ok'
}

export type EtatStock = 'rupture' | 'alerte' | 'ok' | 'nonSuivi'

/**
 * Requête du catalogue TELLE QUE LA LIT LA RÉPLIQUE.
 *
 * ⭐ Plus riche que `ItemQuery` du contrat serveur, volontairement : cette liste
 * ne passe par aucun réseau, ses tris et son filtre de stock n'ont donc pas à
 * exister côté API. Élargir le contrat partagé pour des champs que le serveur
 * ignorerait donnerait une promesse qu'il ne tient pas.
 */
export interface RequeteArticles extends Omit<Partial<ItemQuery>, 'sort'> {
  sort?: string
  /** `attention` = rupture OU sous le seuil, ce que compte le tableau de bord. */
  stock?: EtatStock | 'attention'
  /**
   * `manquant` = PRODUITS sans prix d'achat — ce que signale le bénéfice net
   * du tableau de bord. Les prestations n'y figurent pas : une coupe n'a pas
   * de prix d'achat à compléter.
   */
  prixAchat?: 'manquant'
}

const TRIS_ARTICLE: Record<string, (a: Item, b: Item) => number> = {
  name: (a, b) => a.name.localeCompare(b.name, 'fr'),
  '-name': (a, b) => b.name.localeCompare(a.name, 'fr'),
  salePrice: (a, b) => a.salePrice - b.salePrice,
  '-salePrice': (a, b) => b.salePrice - a.salePrice,
  sku: (a, b) => (a.sku ?? '').localeCompare(b.sku ?? '', 'fr'),
  '-sku': (a, b) => (b.sku ?? '').localeCompare(a.sku ?? '', 'fr'),
  categoryName: (a, b) => (a.categoryName ?? '').localeCompare(b.categoryName ?? '', 'fr'),
  '-categoryName': (a, b) => (b.categoryName ?? '').localeCompare(a.categoryName ?? '', 'fr'),
  taxRate: (a, b) => a.taxRate - b.taxRate,
  '-taxRate': (a, b) => b.taxRate - a.taxRate,
  // Les prestations d'abord en ordre croissant : dans une boutique qui en
  // propose peu, c'est ce qu'on cherche en triant sur cette colonne.
  kind: (a, b) => a.kind.localeCompare(b.kind),
  '-kind': (a, b) => b.kind.localeCompare(a.kind),
  status: (a, b) => a.status.localeCompare(b.status),
  '-status': (a, b) => b.status.localeCompare(a.status),
  // Des horodatages ISO : l'ordre alphabétique EST l'ordre chronologique.
  createdAt: (a, b) => a.createdAt.localeCompare(b.createdAt),
  '-createdAt': (a, b) => b.createdAt.localeCompare(a.createdAt),
}

export async function searchItemsLocal(
  storeId: string,
  query: RequeteArticles,
): Promise<Page<Item>> {
  const db = dbFor(storeId)
  const [rows, index, niveaux] = await Promise.all([
    db.items.toArray(),
    libelles(storeId),
    db.stockLevels.toArray(),
  ])
  let list = rows.map((r) => toItem(r, index.categories, index.suppliers))

  // Les quantités vivent dans une autre table : on les indexe une fois, pour
  // le filtre comme pour le tri.
  const quantites = new Map(niveaux.map((n) => [String(n['item_id']), Number(n['quantity'] ?? 0)]))
  const quantite = (i: Item): number => quantites.get(i.id) ?? 0

  if (query.kind) list = list.filter((i) => i.kind === query.kind)
  if (query.status) list = list.filter((i) => i.status === query.status)
  if (query.supplierId) list = list.filter((i) => i.supplierId === query.supplierId)

  if (query.stock) {
    list = list.filter((i) => {
      const etat = etatDeStock(i, quantite(i))
      return query.stock === 'attention' ? etat === 'rupture' || etat === 'alerte' : etat === query.stock
    })
  }

  if (query.prixAchat === 'manquant') {
    list = list.filter((i) => i.kind === 'product' && i.purchasePrice <= 0)
  }

  if (query.categoryId) {
    // Inclut les sous-catégories, comme le serveur : filtrer « Informatique »
    // doit ramener les ordinateurs. Le chemin matérialisé est répliqué, on
    // s'en sert de la même façon ici.
    const cats = await db.categories.toArray()
    const racine = cats.find((c) => c.id === query.categoryId)
    const chemin = String(racine?.['path'] ?? '')
    const retenues = new Set(
      cats
        .filter((c) => c.id === query.categoryId || String(c['path'] ?? '').startsWith(`${chemin}/`))
        .map((c) => c.id),
    )
    list = list.filter((i) => i.categoryId && retenues.has(i.categoryId))
  }

  if (query.q?.trim()) {
    const needle = fold(query.q)
    list = list.filter((i) =>
      [i.name, i.sku, i.barcode, i.categoryName].some((f) => fold(f).includes(needle)),
    )
  }

  // Le tri par stock ne peut pas vivre dans la table ci-dessus : il a besoin
  // des quantités, qui n'appartiennent pas à l'article.
  if (query.sort === 'stock' || query.sort === '-stock') {
    const sens = query.sort.startsWith('-') ? -1 : 1
    list.sort((a, b) => sens * (quantite(a) - quantite(b)))
  } else {
    list.sort(TRIS_ARTICLE[query.sort ?? 'name'] ?? TRIS_ARTICLE['name']!)
  }

  const offset = query.offset ?? 0
  const limit = query.limit ?? 50
  return { data: list.slice(offset, offset + limit), total: list.length, limit, offset }
}

export async function getItemLocal(storeId: string, id: string): Promise<Item | null> {
  const row = await dbFor(storeId).items.get(id)
  if (!row) return null
  const index = await libelles(storeId)
  return toItem(row, index.categories, index.suppliers)
}

export async function searchSuppliersLocal(
  storeId: string,
  query: Partial<SupplierQuery>,
): Promise<Page<Supplier>> {
  const db = dbFor(storeId)
  const [rows, items] = await Promise.all([db.suppliers.toArray(), db.items.toArray()])

  const comptes = new Map<string, number>()
  for (const i of items) {
    const sid = i['supplier_id'] as string | null
    if (sid) comptes.set(sid, (comptes.get(sid) ?? 0) + 1)
  }

  let list = rows.map((r) => toSupplier(r, comptes.get(r.id) ?? 0))

  if (query.city) list = list.filter((s) => s.city === query.city)
  if (query.q?.trim()) {
    const needle = fold(query.q)
    list = list.filter((s) =>
      [s.companyName, s.contactName, s.email, s.phone, s.city, s.code].some((f) =>
        fold(f).includes(needle),
      ),
    )
  }

  list.sort((a, b) =>
    query.sort === '-companyName'
      ? b.companyName.localeCompare(a.companyName, 'fr')
      : a.companyName.localeCompare(b.companyName, 'fr'),
  )

  const offset = query.offset ?? 0
  const limit = query.limit ?? 50
  return { data: list.slice(offset, offset + limit), total: list.length, limit, offset }
}

export async function getSupplierLocal(storeId: string, id: string): Promise<Supplier | null> {
  const db = dbFor(storeId)
  const row = await db.suppliers.get(id)
  if (!row) return null
  const items = await db.items.toArray()
  const compte = items.filter((i) => i['supplier_id'] === id).length
  return toSupplier(row, compte)
}

/**
 * Les catégories de la réplique, à plat.
 *
 * ⭐ L'agenda en tire ses colonnes : une par catégorie de SERVICES, et une
 * ligne par poste à l'intérieur. Lues sur l'appareil, jamais par le réseau —
 * le planning doit se dessiner au comptoir même sans connexion.
 */
export async function categoriesLocales(storeId: string): Promise<Category[]> {
  const rows = await dbFor(storeId).categories.toArray()
  return rows
    .map(toCategory)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'fr'))
}
